import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { chatApi } from "@/api/chat/chatApi.js";
import { getRealtimeHubConnection, startRealtimeHubConnection } from "@/utils/realtimeHub.js";
import { normalizeChatMessage, deduplicateMessages } from "@/utils/chat/chatNormalizer.js";

/**
 * Dedicated single-responsibility hook for Guest room chat.
 */
export const useGuestChatSession = ({
  guestInfo = null,
  defaultDepartment = "Reception",
} = {}) => {
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const guestInfoRef = useRef(guestInfo);
  const conversationRef = useRef(conversation);
  const typingTimerRef = useRef(null);

  useEffect(() => {
    guestInfoRef.current = guestInfo;
  }, [guestInfo]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  const sessionId = guestInfo?.sessionId || guestInfo?.id;
  const organizationId = guestInfo?.organizationId;

  const loadInitialData = useCallback(async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const [convRes, historyRes, unreadRes] = await Promise.allSettled([
        chatApi.getGuestConversation({
          organizationId: organizationId || undefined,
          guestSessionId: sessionId,
          department: defaultDepartment,
          guestName: guestInfo?.guestName,
          roomName: guestInfo?.roomName,
        }),
        chatApi.getGuestHistory({
          organizationId: organizationId || undefined,
          guestSessionId: sessionId,
        }),
        chatApi.getGuestUnreadCount({
          organizationId: organizationId || undefined,
          guestSessionId: sessionId,
        }),
      ]);

      if (convRes.status === "fulfilled") {
        const c = convRes.value?.data?.data || convRes.value?.data;
        if (c) setConversation(c);
      }

      if (historyRes.status === "fulfilled") {
        const rawList = Array.isArray(historyRes.value?.data?.data)
          ? historyRes.value.data.data
          : Array.isArray(historyRes.value?.data)
          ? historyRes.value.data
          : [];
        setMessages(deduplicateMessages(rawList));
      }

      if (unreadRes.status === "fulfilled") {
        const u = typeof unreadRes.value?.data?.data === "number"
          ? unreadRes.value.data.data
          : typeof unreadRes.value?.data === "number"
          ? unreadRes.value.data
          : 0;
        setUnreadCount(u);
      }
    } catch (err) {
      console.error("[useGuestChatSession] Error loading initial chat:", err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, organizationId, defaultDepartment, guestInfo?.guestName, guestInfo?.roomName]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const sendMessage = useCallback(
    async ({ content, targetDepartment = defaultDepartment, attachmentUrl = null, attachmentType = null }) => {
      if (!content || !content.trim() || !sessionId) return;

      setSending(true);
      try {
        const payload = {
          organizationId: guestInfoRef.current?.organizationId,
          guestSessionId: sessionId,
          conversationId: conversationRef.current?.id,
          targetType: "Department",
          targetDepartment,
          roomName: guestInfoRef.current?.roomName,
          senderDisplayName: guestInfoRef.current?.guestName || `Room ${guestInfoRef.current?.roomName || ""}`,
          content: content.trim(),
          attachmentUrl,
          attachmentType,
        };

        const res = await chatApi.sendGuestMessage(payload);
        const msg = res?.data?.data || res?.data;
        if (msg) {
          const norm = normalizeChatMessage(msg);
          setMessages((prev) => deduplicateMessages([...prev, norm]));
          if (norm.conversationId && (!conversationRef.current?.id || conversationRef.current.id !== norm.conversationId)) {
            setConversation((prev) => (prev ? { ...prev, id: norm.conversationId } : { id: norm.conversationId }));
          }
        }
      } catch (err) {
        console.error("[useGuestChatSession] Failed to send message:", err);
        toast.error("Failed to send message.");
      } finally {
        setSending(false);
      }
    },
    [sessionId, defaultDepartment]
  );

  const sendTyping = useCallback(
    async (typing) => {
      try {
        if (!sessionId) return;
        const connection = getRealtimeHubConnection({ guestSessionId: sessionId });
        if (connection && connection.state === "Connected") {
          await connection.invoke("SendChatTyping", {
            conversationId: conversationRef.current?.id || null,
            targetDepartment: defaultDepartment,
            guestSessionId: sessionId,
            isTyping: typing,
          });
        }
      } catch {}
    },
    [sessionId, defaultDepartment]
  );

  // Realtime SignalR event listeners
  useEffect(() => {
    if (!sessionId) return;

    const hubOptions = { guestSessionId: sessionId };
    let connection;
    try {
      connection = getRealtimeHubConnection(hubOptions);
      startRealtimeHubConnection(hubOptions).catch(() => {});
    } catch {
      return;
    }

    const handleReceiveChatMessage = (incomingMsg) => {
      if (!incomingMsg) return;
      const norm = normalizeChatMessage(incomingMsg);
      if (!norm) return;

      const currentSid = String(sessionId).toLowerCase();
      const msgSid = String(norm.guestSessionId || norm.senderGuestSessionId || "").toLowerCase();

      // Check if message belongs to this guest session
      if (msgSid && msgSid !== currentSid) return;

      const isSenderMe = norm.senderType === "Guest";

      setMessages((prev) => deduplicateMessages([...prev, norm]));
      if (norm.conversationId && (!conversationRef.current?.id || conversationRef.current.id !== norm.conversationId)) {
        setConversation((prev) => (prev ? { ...prev, id: norm.conversationId } : { id: norm.conversationId }));
      }

      if (!isSenderMe) {
        chatApi.markGuestAsRead({
          organizationId: incomingMsg.organizationId || organizationId,
          guestSessionId: sessionId,
          conversationId: norm.conversationId,
        }).catch(() => {});
      }
    };

    const handleChatTyping = (data) => {
      if (!data) return;
      const sid = String(data.guestSessionId || data.GuestSessionId || "").toLowerCase();
      if (sid && sid !== String(sessionId).toLowerCase()) return;
      if (data.senderType === "Guest") return;

      const isTyping = Boolean(data.isTyping ?? data.IsTyping);
      if (isTyping) {
        setOtherUserTyping({
          displayName: data.senderDisplayName || data.SenderDisplayName || "Hotel Staff",
        });
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setOtherUserTyping(null), 3000);
      } else {
        setOtherUserTyping(null);
      }
    };

    connection.on("ReceiveChatMessage", handleReceiveChatMessage);
    connection.on("receivechatmessage", handleReceiveChatMessage);
    connection.on("ChatTyping", handleChatTyping);
    connection.on("chattyping", handleChatTyping);

    return () => {
      connection.off("ReceiveChatMessage", handleReceiveChatMessage);
      connection.off("receivechatmessage", handleReceiveChatMessage);
      connection.off("ChatTyping", handleChatTyping);
      connection.off("chattyping", handleChatTyping);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [sessionId, organizationId]);

  return {
    messages,
    conversation,
    loading,
    sending,
    otherUserTyping,
    unreadCount,
    sendMessage,
    sendTyping,
    loadInitialData,
  };
};
